import { useMemo, useState } from "react";

import {
  HARBOUR_ENTRY,
  PORT_SIGNALS,
  TECHNIQUES,
  TECHNIQUE_IDS,
} from "@nav-games/pilotage";

import { DangerBearingFigure } from "../../ui/DangerBearingFigure";
import { LeadingLineFigure } from "../../ui/LeadingLineFigure";
import { SignalStack } from "../../ui/SignalStack";
import { ThreePointFixCalculator } from "../../ui/ThreePointFixCalculator";
import { TransitLineFigure } from "../../ui/TransitLineFigure";

interface PilotageProps {
  onNavigate: (to: string) => void;
}

export function Pilotage({ onNavigate }: PilotageProps) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>Pilotage</h2>
      <p className="muted">
        Eyes-out navigation when you're close to a coast — using
        visible marks rather than instruments to keep clear of
        hazards and find the way in. Covers the four classical
        techniques (transits, leading lines, danger bearings,
        clearing transits) plus harbour-entry planning and port
        traffic signals.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Play</h3>
        <button onClick={() => onNavigate("/play/pilotage/three-point-fix")}>
          Rate the three-point fix
        </button>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          Three bearings, three position lines, a cocked-hat triangle —
          rate the fix quality from the geometry. Reveal explains the
          mean-side-length bands.
        </p>
      </div>

      <ThreePointFixCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Position fix by transits</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Two transits crossing at right angles give a perfect fix
          with no compass error. A natural transit is any pair of
          identifiable charted features that happen to line up; a
          charted transit is a deliberately-positioned pair of
          marks (often beacons).
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <TransitLineFigure size={420} />
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Leading lines</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Officially-charted leading marks (front lower, rear
          higher) define a safe channel. Keep them in vertical
          alignment as you approach — drift right of the line, the
          rear mark slides LEFT relative to the front, and you
          alter to port to bring them back together. (Or vice
          versa.)
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LeadingLineFigure size={420} />
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Danger bearings</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Pick a prominent mark. Draw the bearing from the outer
          edge of a hazard to that mark — that's your limit. Keep
          the actual bearing on the SAFE side of the limit. <strong>NLT</strong>
          (no less than) means "stay greater than"; <strong>NMT</strong>{" "}
          (no more than) means "stay less than".
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <DangerBearingFigure size={420} />
        </div>
      </div>

      <DangerBearingCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Techniques in detail</h3>
        {TECHNIQUE_IDS.map((id) => {
          const t = TECHNIQUES[id]!;
          return (
            <details key={id} className="card" style={{ marginBottom: 6 }}>
              <summary
                style={{
                  cursor: "pointer",
                  listStyle: "none",
                  fontWeight: 600,
                }}
              >
                {t.name}
                <div
                  className="small muted"
                  style={{ fontWeight: 400, marginTop: 2 }}
                >
                  {t.summary}
                </div>
              </summary>
              <p className="small muted" style={{ margin: "8px 0 4px" }}>
                {t.description}
              </p>
              <p className="small muted" style={{ margin: "4px 0" }}>
                <strong>When to use:</strong> {t.whenToUse}
              </p>
              <p className="small" style={{ margin: "10px 0 4px" }}>
                <strong>Steps</strong>
              </p>
              <ol className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
                {t.steps.map((s, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {s}
                  </li>
                ))}
              </ol>
              <p className="small" style={{ margin: "10px 0 4px" }}>
                <strong>Pitfalls</strong>
              </p>
              <ul className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
                {t.pitfalls.map((p, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {p}
                  </li>
                ))}
              </ul>
            </details>
          );
        })}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Harbour-entry pre-flight</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Eight items every skipper runs through before committing
          to an entrance. The pre-departure checklist covers
          general kit + crew; this one is about the destination.
        </p>
        {HARBOUR_ENTRY.map((h) => (
          <div
            key={h.id}
            style={{
              borderTop: "1px solid var(--border)",
              paddingTop: 10,
              marginTop: 10,
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>{h.title}</p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              {h.detail}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Source:</strong> {h.source}
            </p>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>International Port Traffic Signals</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The standard five-signal IPTS scheme used at most
          commercial harbours worldwide. Always check the local
          almanac — some ports add extra signals or modify the
          standard.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 12,
          }}
        >
          {PORT_SIGNALS.map((s) => (
            <div key={s.id} style={{ textAlign: "center" }}>
              <SignalStack signal={s} />
              <div
                className="small"
                style={{ marginTop: 6, fontWeight: 600 }}
              >
                {s.shortMeaning}
              </div>
              <div className="small muted" style={{ marginTop: 4 }}>
                {s.meaning}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}

// ─── Danger-bearing calculator ──────────────────────────────────

/// Given a mark's bearing as currently observed plus the charted
/// limit + direction (NLT or NMT), report whether the vessel is
/// safe or in the danger sector.
function DangerBearingCalculator() {
  const [mode, setMode] = useState<"NLT" | "NMT">("NLT");
  const [limitBearing, setLimitBearing] = useState("035");
  const [observedBearing, setObservedBearing] = useState("045");

  const result = useMemo(() => {
    const limit = parseFloat(limitBearing);
    const obs = parseFloat(observedBearing);
    if (Number.isNaN(limit) || Number.isNaN(obs)) return null;
    if (limit < 0 || limit > 360 || obs < 0 || obs > 360) return null;
    const safe = mode === "NLT" ? obs > limit : obs < limit;
    const marginDeg = mode === "NLT" ? obs - limit : limit - obs;
    return { safe, marginDeg };
  }, [mode, limitBearing, observedBearing]);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Danger-bearing check</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Enter the limit you've worked out from the chart, the type
        (NLT or NMT), and the current bearing to the mark from a
        hand-bearing compass. Tells you whether you're still on the
        safe side and by how much.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 8,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="small muted">Mode</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as "NLT" | "NMT")}
            style={{
              padding: "8px 10px",
              fontSize: 15,
              border: "1px solid var(--border-strong)",
              borderRadius: "var(--radius-sm)",
              background: "var(--bg-card)",
              color: "var(--text)",
            }}
          >
            <option value="NLT">NLT — bearing must be GREATER than limit</option>
            <option value="NMT">NMT — bearing must be LESS than limit</option>
          </select>
        </label>
        <Field
          label="Limit bearing (°)"
          value={limitBearing}
          onChange={setLimitBearing}
        />
        <Field
          label="Observed bearing (°)"
          value={observedBearing}
          onChange={setObservedBearing}
        />
      </div>
      {result && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 6,
            background: result.safe
              ? "rgba(44, 122, 59, 0.10)"
              : "rgba(184, 48, 42, 0.10)",
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: 700,
              color: result.safe ? "var(--success)" : "var(--danger)",
            }}
          >
            {result.safe ? "✓ Safe" : "✗ In the danger sector"}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            {result.safe
              ? `Margin: ${Math.abs(result.marginDeg).toFixed(1)}° clear of the limit.`
              : `You are ${Math.abs(result.marginDeg).toFixed(1)}° on the wrong side. Alter course away from the hazard immediately.`}
          </p>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="small muted">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 15,
        }}
      />
    </label>
  );
}

// Signal stack lives in apps/web/src/ui/SignalStack.tsx so the
// Reference > Port-signals tab can reuse the same animated component.
